import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Waves, ShoppingBag, Tag, Repeat, Check } from "lucide-react";
import { useI18n, LANG_LABELS } from "../i18n/I18nContext";
import { useAuth } from "../context/AuthContext";
import { ISLANDS } from "../lib/data";
import { Button } from "../components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import api from "../lib/api";

export default function Onboarding() {
  const { t, lang, setLang, languages } = useI18n();
  const { refresh } = useAuth();
  const navigate = useNavigate();
  const [step, setStep] = useState(0);
  const [island, setIsland] = useState("");
  const [municipality, setMunicipality] = useState("");
  const [intent, setIntent] = useState("");
  const munis = ISLANDS.find((i) => i.id === island)?.municipalities || [];

  const finish = async () => {
    try { await api.post("/auth/onboard", { language: lang, island, municipality }); } catch {}
    await refresh();
    navigate("/");
  };

  return (
    <div className="min-h-[calc(100vh-4rem)] flex items-center justify-center p-6 bg-gradient-to-br from-[#F8FAF9] to-teal-50">
      <div className="w-full max-w-md bg-white rounded-3xl border border-slate-200 shadow-xl p-8">
        <div className="flex items-center gap-2 mb-6 justify-center">
          <span className="h-10 w-10 grid place-items-center rounded-xl bg-[#008080] text-white"><Waves className="h-5 w-5" /></span>
        </div>
        <div className="flex gap-1.5 mb-6 justify-center">
          {[0, 1, 2].map((s) => <span key={s} className={`h-1.5 rounded-full transition-all ${s === step ? "w-8 bg-[#008080]" : "w-2 bg-slate-200"}`} />)}
        </div>

        {step === 0 && (
          <div className="cm-fade-up">
            <h1 className="font-display font-bold text-2xl text-center">{t("onboarding.welcome")}</h1>
            <p className="text-center text-sm text-slate-500 mt-1 mb-6">{t("onboarding.chooseLang")}</p>
            <div className="space-y-3">
              {languages.map((l) => (
                <button key={l} data-testid={`onboard-lang-${l}`} onClick={() => setLang(l)}
                  className={`w-full flex items-center justify-between px-4 py-3 rounded-xl border transition-colors ${lang === l ? "border-[#008080] bg-teal-50" : "border-slate-200 hover:bg-slate-50"}`}>
                  <span className="font-medium">{LANG_LABELS[l]}</span>
                  {lang === l && <Check className="h-5 w-5 text-[#008080]" />}
                </button>
              ))}
            </div>
            <Button data-testid="onboard-next-0" onClick={() => setStep(1)} className="w-full mt-6 h-11 bg-[#008080] hover:bg-[#006666]">{t("onboarding.next")}</Button>
          </div>
        )}

        {step === 1 && (
          <div className="cm-fade-up">
            <h1 className="font-display font-bold text-2xl text-center">{t("onboarding.where")}</h1>
            <p className="text-center text-sm text-slate-500 mt-1 mb-6">{t("onboarding.whereDesc")}</p>
            <div className="space-y-4">
              <div>
                <label className="text-sm font-medium">{t("onboarding.island")}</label>
                <Select value={island} onValueChange={(v) => { setIsland(v); setMunicipality(""); }}>
                  <SelectTrigger data-testid="onboard-island-select" className="mt-1 h-11"><SelectValue placeholder={t("onboarding.island")} /></SelectTrigger>
                  <SelectContent>{ISLANDS.map((i) => <SelectItem key={i.id} value={i.id}>{i.name}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div>
                <label className="text-sm font-medium">{t("onboarding.municipality")}</label>
                <Select value={municipality} onValueChange={setMunicipality} disabled={!island}>
                  <SelectTrigger data-testid="onboard-muni-select" className="mt-1 h-11"><SelectValue placeholder={t("onboarding.municipality")} /></SelectTrigger>
                  <SelectContent>{munis.map((m) => <SelectItem key={m} value={m}>{m}</SelectItem>)}</SelectContent>
                </Select>
              </div>
            </div>
            <Button data-testid="onboard-next-1" onClick={() => setStep(2)} disabled={!island} className="w-full mt-6 h-11 bg-[#008080] hover:bg-[#006666]">{t("onboarding.next")}</Button>
          </div>
        )}

        {step === 2 && (
          <div className="cm-fade-up">
            <h1 className="font-display font-bold text-2xl text-center">{t("onboarding.whatToDo")}</h1>
            <div className="grid gap-3 mt-6">
              {[{ id: "buy", icon: ShoppingBag, label: t("onboarding.buy") }, { id: "sell", icon: Tag, label: t("onboarding.sellO") }, { id: "both", icon: Repeat, label: t("onboarding.both") }].map((o) => (
                <button key={o.id} data-testid={`onboard-intent-${o.id}`} onClick={() => setIntent(o.id)}
                  className={`flex items-center gap-3 px-4 py-4 rounded-xl border transition-colors ${intent === o.id ? "border-[#008080] bg-teal-50" : "border-slate-200 hover:bg-slate-50"}`}>
                  <o.icon className="h-6 w-6 text-[#008080]" /><span className="font-medium">{o.label}</span>
                </button>
              ))}
            </div>
            <Button data-testid="onboard-finish" onClick={finish} disabled={!intent} className="w-full mt-6 h-11 bg-[#FF9F1C] hover:bg-[#e88f13] text-white">{t("onboarding.finish")}</Button>
          </div>
        )}
      </div>
    </div>
  );
}
